import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import MovieCard from '@/components/feature/MovieCard';
import Reveal from '@/components/feature/Reveal';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import { categoryNames } from '@/mocks/categories';
import useSeo from '@/hooks/useSeo';

function ShareModal({ title, onClose }: { title: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-foreground-950/60" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-lg bg-background-50 p-6 animate-fade-in-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-foreground-950">分享《{title}》</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer" aria-label="关闭">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="flex items-center gap-2 p-3 rounded-md bg-background-100 border border-background-200 mb-5">
          <i className="ri-link text-foreground-400" />
          <span className="flex-1 text-xs text-foreground-600 truncate">{typeof window !== 'undefined' ? window.location.href : ''}</span>
          <button type="button" onClick={copyLink} className="px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            {copied ? '已复制' : '复制'}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: 'ri-weibo-fill', label: '微博' },
            { icon: 'ri-twitter-x-fill', label: 'X' },
            { icon: 'ri-facebook-circle-fill', label: 'Facebook' },
          ].map((s) => (
            <button
              key={s.label}
              type="button"
              onClick={copyLink}
              className="flex flex-col items-center gap-2 py-3 rounded-md bg-background-100 hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              <i className={`${s.icon} text-2xl text-secondary-700`} />
              <span className="text-xs text-foreground-600">{s.label}</span>
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs text-foreground-400 text-center">演示环境仅支持复制链接，未接入真实社交分享接口</p>
      </div>
    </div>
  );
}

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const [expanded, setExpanded] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const related = useMemo(() => (movie ? getRelatedMovies(movie, 4) : []), [movie]);

  useSeo({
    title: movie ? `【${movie.title}】剧情介绍与影评 - 美国伦理片` : '影片不存在 - 美国伦理片',
    description: movie
      ? `《${movie.title}》（${movie.originalTitle}，${movie.year}）是由 ${movie.director} 执导的美国伦理电影，本页提供完整剧情介绍、影片资料与专业影评，帮助你了解这部作品。`
      : '你访问的影片不存在或已下架，请返回首页浏览其他美国伦理电影。',
    keywords: movie ? `${movie.title},${movie.title}影评,${movie.title}剧情,美国伦理片,${categoryNames[movie.category] || ''}` : '美国伦理片',
  });

  if (!movie) {
    return (
      <Layout>
        <div className="px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
            <i className="ri-film-line text-3xl" />
          </span>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3">该影片不存在或已下架</h1>
          <p className="text-sm text-foreground-500 mb-6">你访问的影片可能已移除，或链接有误。</p>
          <Link to="/" className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const infoRows = [
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.cast.join('、') },
    { label: '上映年份', value: `${movie.year} 年` },
    { label: '影片时长', value: `${movie.duration} 分钟` },
    { label: '国家 / 地区', value: movie.region },
    { label: '影片类型', value: movie.genres.join(' / ') },
    { label: '语言', value: '英语' },
    { label: '编辑评分', value: `${movie.rating.toFixed(1)} / 10` },
  ];

  return (
    <Layout>
      <div className="px-4 md:px-8 pt-6">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/category" className="hover:text-primary-600 cursor-pointer">影片分类</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800 truncate">{movie.title}</span>
        </nav>
      </div>

      {/* 头部 */}
      <section className="px-4 md:px-8 py-8 bg-background-50 border-b border-background-200 mt-4">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-full md:w-64 shrink-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img src={movie.poster} alt={`${movie.title} 海报`} title={`${movie.title} - 美国伦理电影`} className="w-full h-full object-top" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-2">{movie.title}</h1>
            <p className="text-sm text-foreground-500 mb-4">{movie.originalTitle}</p>
            <div className="flex items-center gap-4 mb-5 flex-wrap text-sm text-foreground-600">
              <span className="flex items-center gap-1.5 text-accent-600 font-bold text-lg">
                <i className="ri-star-fill" /> {movie.rating.toFixed(1)}
              </span>
              <span>{movie.year} 年</span>
              <span>{movie.region}</span>
              <span>{movie.duration} 分钟</span>
            </div>

            <div className="flex items-center gap-2 mb-5 flex-wrap">
              {movie.genres.map((g) => (
                <span key={g} className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">{g}</span>
              ))}
              <Link to={`/category?cat=${movie.category}`} className="px-3 py-1 rounded-md bg-accent-100 text-accent-800 text-xs hover:bg-accent-200 transition-colors cursor-pointer">
                {categoryNames[movie.category]}
              </Link>
            </div>

            <p className="text-sm leading-7 text-foreground-600 mb-6 line-clamp-3">{movie.synopsis}</p>

            <div className="flex items-center gap-3 flex-wrap">
              <Link to="/category" className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                浏览同类影片
              </Link>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="px-5 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 hover:border-primary-300 transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line" /> 分享
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8 min-w-0">
            {/* 剧情简介 */}
            <section>
              <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-book-open-line text-primary-500" /> 剧情简介
              </h2>
              <div className="rounded-lg bg-background-50 border border-background-200 p-5">
                <p className={`text-sm md:text-base leading-8 text-foreground-700 ${expanded ? '' : 'line-clamp-4'}`}>
                  {movie.synopsis} {movie.synopsis}
                </p>
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-3 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer"
                >
                  {expanded ? '收起全文' : '展开阅读全文'}
                  <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                </button>
              </div>
            </section>

            {/* 编辑影评 */}
            <section>
              <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-quill-pen-line text-primary-500" /> 编辑影评
              </h2>
              <article className="rounded-lg bg-background-50 border border-background-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-user-star-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">本站编辑部</p>
                    <p className="text-xs text-foreground-400">专业影评 · {movie.year}</p>
                  </div>
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-3">{movie.editorReviewTitle}</h3>
                <p className="text-sm md:text-base leading-8 text-foreground-700">{movie.editorReview}</p>
              </article>
            </section>

            {/* 相关推荐 */}
            <section>
              <h2 className="text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-movie-2-line text-primary-500" /> 相关推荐
              </h2>
              {related.length === 0 ? (
                <p className="text-sm text-foreground-500">暂无推荐内容</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {related.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* 侧栏资料表 */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="rounded-lg bg-background-50 border border-background-200 p-5">
                <h2 className="text-base font-semibold text-foreground-950 mb-4">影片资料</h2>
                <dl className="space-y-3.5">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex gap-3 text-sm">
                      <dt className="w-20 shrink-0 text-foreground-400">{row.label}</dt>
                      <dd className="flex-1 text-foreground-800 text-right">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5">
                <h3 className="text-sm font-semibold text-secondary-900 mb-3 flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line" /> 所属分类
                </h3>
                <div className="flex flex-wrap gap-2">
                  {movie.tags.map((t) => (
                    <span key={t} className="px-2.5 py-1 rounded-md bg-background-50 text-secondary-800 text-xs border border-secondary-200">{t}</span>
                  ))}
                </div>
                <Link
                  to={`/category?cat=${movie.category}`}
                  className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  查看「{categoryNames[movie.category]}」全部影片 <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <div className="rounded-lg bg-accent-50 border border-accent-200 p-5">
                <h3 className="text-sm font-semibold text-accent-900 mb-2 flex items-center gap-1.5">
                  <i className="ri-information-line" /> 演示说明
                </h3>
                <p className="text-xs leading-6 text-accent-900/80">
                  本站所有影片信息、评分与影评均为演示数据，本页不提供在线播放或下载功能，仅用于内容展示。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {shareOpen && <ShareModal title={movie.title} onClose={() => setShareOpen(false)} />}
    </Layout>
  );
}